import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import campus from "../../assets/campus-sign.webp";

export const Route = createFileRoute("/es/primaria")({
  loader: async ({ context }) => {
    const data = await safeLoader("es.primaria.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "grundskola", "es") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl("/es/primaria") },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:locale", content: "es_ES" },
        ...ogImage(),
      ],
      links: seoLinks("/es/primaria"),
    };
  },
  component: Primaria,
});

function Primaria() {
  const { text } = useContent("grundskola");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <Prose>
        <p>
          El colegio sigue el currículo sueco y cuenta con profesorado suecoparlante que trabaja para
          crear un entorno de aprendizaje seguro y estimulante. En nuestras instalaciones impartimos
          desde la clase preescolar hasta 6.º curso, y para secundaria y bachillerato ofrecemos
          acompañamiento a través de Sofia Distans y Hermods.
        </p>

        <h2>El aprendizaje del alumnado en el centro</h2>
        <p>
          El colegio se centra en desarrollar las competencias académicas, sociales y personales del
          alumnado a través de distintas asignaturas y actividades. Los alumnos reciben una educación
          sueca de calidad que les da una base sólida para su escolarización posterior.
        </p>

        <h2>Continuidad educativa</h2>
        <p>
          El colegio sigue el currículo sueco y utiliza el sueco como lengua de enseñanza, lo que
          permite a los alumnos continuar su educación sueca viviendo en España. Así, la transición
          es más sencilla si regresan a Suecia o continúan en otros colegios internacionales.
        </p>

        <img
          src={campus}
          alt="Cartel con el logotipo de Swedish International School en la entrada del colegio"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={1600}
          height={1067}
        />

        <h2>Tradiciones y cultura suecas</h2>
        <p>
          El colegio utiliza las celebraciones, canciones y tradiciones suecas para acercar al
          alumnado a la cultura y la historia de Suecia. Así los alumnos conocen su herencia cultural
          y se sienten parte de las tradiciones suecas aunque vivan en España.
        </p>
        <p>
          El colegio también tiene un fuerte sentido de comunidad y ofrece a los alumnos la
          oportunidad de conocer a otros niños y familias suecas que viven en España.
        </p>

        <h2>Un día escolar con nosotros</h2>
        <p>
          La jornada empieza con una reunión de clase y continúa con las asignaturas del currículo,
          con recreos al aire libre y la comida en el colegio. Las tardes se dedican a menudo a
          asignaturas prácticas y creativas, educación física y trabajo en grupos pequeños. Después
          de la jornada escolar hay{" "}
          <a className="text-primary link-quiet" href="/es/actividades-extraescolares">actividades extraescolares</a>{" "}
          para quien quiera quedarse más tiempo.
        </p>
        <p>
          Los grupos son reducidos, así que el profesorado tiene tiempo para ver a cada alumno y
          adaptar las tareas a su nivel. Las herramientas digitales complementan los libros y la
          escritura, no los sustituyen.
        </p>

        <h2>Asignaturas y evaluación</h2>
        <p>
          La enseñanza sigue los programas del currículo nacional sueco. El español está en el
          horario todas las semanas y el inglés se imparte conforme al currículo. El progreso se hace
          un seguimiento continuo y se comunica a las familias en reuniones de desarrollo y
          valoraciones escritas, para que sepáis dónde está vuestro hijo ante un posible regreso a
          Suecia.
        </p>

        <h2>Apoyo y bienestar</h2>
        <p>
          El tutor es el primer contacto de la familia en el día a día. Cuando hace falta, participa
          el{" "}
          <a className="text-primary link-quiet" href="/es/bienestar-del-alumnado">equipo de bienestar del alumnado</a>{" "}
          para valoraciones y adaptaciones. Podéis leer qué podemos y qué no podemos ofrecer en la
          página de{" "}
          <a className="text-primary link-quiet" href="/es/apoyo-educativo">apoyo educativo</a>.
        </p>

        <h2>Cómo empezar</h2>
        <p>
          Las solicitudes se reciben de forma continua y es posible empezar a mitad de curso. Las
          fechas del curso están en el{" "}
          <a className="text-primary link-quiet" href="/es/calendario-escolar">calendario escolar</a>, las tarifas
          por curso en la{" "}
          <a className="text-primary link-quiet" href="/es/tarifas">página de tarifas</a> y el formulario en{" "}
          <a className="text-primary link-quiet" href="/es/solicitud">solicitud</a>. Con mucho gusto organizamos
          una visita si queréis conocer el colegio.
        </p>
      </Prose>

      <FaqBlock items={faqFor("grundskola", "es")} locale="es" />

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
